/* eslint-disable jsx-a11y/img-redundant-alt */
import React from 'react';
import { Star, User, CheckCircle } from 'lucide-react';
import { useVendorReviews } from '../../services/vendorService';

const VendorReviews = ({ vendorId }) => {
  const { data: reviewsResponse, isLoading, error } = useVendorReviews(vendorId, { per_page: 10 });
  
  // Handle paginated response structure
  // Laravel pagination returns: { data: [...], total: X, per_page: Y, etc. }
  const paginatedData = reviewsResponse?.data;
  const reviews = Array.isArray(paginatedData?.data) 
    ? paginatedData.data 
    : Array.isArray(paginatedData) 
    ? paginatedData 
    : [];
  const totalReviews = paginatedData?.total ?? (Array.isArray(paginatedData) ? paginatedData.length : 0);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-5 w-32 bg-gray-200 rounded animate-pulse"></div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3 animate-pulse">
            <div className="h-3 w-20 bg-gray-200 rounded mb-2"></div>
            <div className="h-3 w-full bg-gray-200 rounded mb-1"></div>
            <div className="h-3 w-3/4 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 border border-gray-200 rounded-lg">
        <p className="text-xs sm:text-sm text-red-600">Failed to load reviews. Please try again later.</p>
      </div>
    );
  }

  if (totalReviews === 0) {
    return (
      <div className="text-center py-8 sm:py-12 border border-gray-200 rounded-lg">
        <Star className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3 sm:mb-4" />
        <h3 className="text-base sm:text-lg font-light text-black mb-1 sm:mb-2">No Reviews Yet</h3>
        <p className="text-xs sm:text-sm text-gray-600 font-light">Be the first to review this vendor after your booking!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <h3 className="text-base sm:text-lg font-light text-black">
          Customer Reviews
        </h3>
        <span className="text-xs sm:text-sm text-gray-600">
          {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
        </span>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {reviews.map((review) => (
          <div key={review.id} className="pb-3 sm:pb-4 border-b border-gray-200 last:border-0 last:pb-0">
            <div className="flex items-start gap-2 sm:gap-3">
              {/* Avatar */}
              <div className="flex-shrink-0">
                {review.client?.profile_image_url ? (
                  <img
                    src={review.client.profile_image_url}
                    alt={review.client.name || 'User'}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Review Content */}
              <div className="flex-1 min-w-0">
                {/* Name and Verified Badge */}
                <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-1.5">
                  <h4 className="text-xs sm:text-sm font-medium text-black">
                    {review.client?.name || 'Anonymous'}
                  </h4>
                  {review.is_verified && (
                    <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                  )}
                </div>

                {/* Rating and Date */}
                <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${
                          star <= review.rating
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(review.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>

                {/* Title */}
                {review.title && (
                  <h5 className="text-xs sm:text-sm font-medium text-black mb-0.5 sm:mb-1">
                    {review.title}
                  </h5>
                )}

                {/* Comment */}
                {review.comment && (
                  <p className="text-xs sm:text-sm text-gray-700 font-light leading-relaxed">
                    {review.comment}
                  </p>
                )}

                {/* Images */}
                {review.images && review.images.length > 0 && (
                  <div className="mt-2 sm:mt-3 grid grid-cols-3 gap-1.5 sm:gap-2">
                    {review.images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Review image ${index + 1}`}
                        className="w-full h-16 sm:h-20 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VendorReviews;

